/**
 * C5 — "<Organisation> invites you to join its portfolio".
 *
 * The interstitial the inventor meets the moment an organisation invites the
 * account they are signed in as. Renamed from AcceptSeatPanel with Story #5648
 * build v2, and rewritten with it: the old card described a licensing
 * transaction ("this pays for your seat"), which is not what accepting does and
 * not what the inventor is being asked to consent to.
 *
 * What accepting does is one thing and three non-things, and the figure states
 * all four before the button:
 *   ✓ it records that you are part of this portfolio, and lets them propose an
 *     IP arrangement on a project you have granted them;
 *   ○ it grants them no project and shows them none of your content;
 *   ○ it gives them no role in your workspace and no say over your projects;
 *   and if the association ends, everything stays yours.
 *
 * "How you got here" is in the card for a reason: this is the only consent
 * screen in the product that appears without the reader having clicked
 * anything, and a consent card nobody can account for is one nobody should
 * trust. There is no link, no token and no email to match — the invitation is
 * addressed to the signed-in account, and a withdrawn one says so rather than
 * vanishing.
 *
 * Presentation and local confirmation only; the page owns the accept call.
 */
import { useState } from 'react'
import { organisationName, possessive, shortDate } from './sharingModel'
import type { PendingSeat } from '../../types'

export interface PortfolioInvitationProps {
  /**
   * null when there is no invitation to answer — a withdrawn one, or a link the
   * inventor kept. The figure names that case explicitly and the card renders
   * it, never nothing at all.
   */
  invitation: PendingSeat | null
  onAccept: (aggregatorId: string) => Promise<void>
  /** Closes the card without answering. Changes nothing on the server. */
  onDismiss: () => void
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '??'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

function Line({ tone, children }: { tone: 'will' | 'wont'; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 border-t border-slate-100 py-2.5 text-[13px] leading-relaxed text-slate-600 first:border-t-0">
      <span
        aria-hidden
        className={`mt-0.5 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg text-[13px] font-extrabold ${
          tone === 'will' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
        }`}
      >
        {tone === 'will' ? '✓' : '○'}
      </span>
      <span>{children}</span>
    </div>
  )
}

export default function PortfolioInvitation({ invitation, onAccept, onDismiss }: PortfolioInvitationProps) {
  const [confirmation, setConfirmation] = useState('')
  const [accepting, setAccepting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  if (!invitation) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Join a portfolio"
        data-part="PortfolioInvitation"
        className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-700"
      >
        <p>
          This invitation is no longer available — ask the organisation to send a
          new one.
        </p>
        <p className="mt-2 text-[12px] text-slate-500">
          Joining a portfolio never shows them your work — sharing is separate,
          and yours to decide.
        </p>
        <button
          type="button"
          onClick={onDismiss}
          className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"
        >
          Close
        </button>
      </div>
    )
  }

  // Never the raw aggregator id: a consent card that says
  // "49ee2468-2041-… invites you to join its portfolio" is one nobody can
  // honestly answer. An organisation with no name is named as such.
  const org = organisationName(
    invitation.aggregator_name, invitation.aggregator_id, invitation.aggregator_holder,
  )
  // "Acme Aggregator's", "Meridian Ventures'". Round 3: this printed the s-less
  // form for every organisation, because the figure's example name ends in s
  // and that had been copied out of it as if it were the rule.
  const orgs = possessive(org)
  const canAccept = confirmation === 'I understand' && !accepting

  const accept = async () => {
    if (!canAccept) return
    setAccepting(true)
    setErrorMessage(null)
    try {
      await onAccept(invitation.aggregator_id)
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Could not accept this invitation.'
      setErrorMessage(message)
    } finally {
      setAccepting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${org} invites you to join its portfolio`}
      data-part="PortfolioInvitation"
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
    >
      <div className="flex items-start gap-3.5 px-6 pb-3.5 pt-5">
        <div
          aria-hidden
          className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-iflow-400 to-iflow-600 text-[19px] font-extrabold text-white"
        >
          {initials(org)}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-[18px] font-extrabold leading-tight text-slate-900">
            {org} invites you to join its portfolio
          </h2>
          <p className="mt-1 text-[12.5px] leading-relaxed text-slate-600">
            You would become an entity in <b className="text-slate-900">{orgs}</b>{' '}
            portfolio. They see <b className="text-slate-900">only what you choose to share</b> —
            nothing about your own account changes.
            {invitation.invited_at && (
              <> Invited {shortDate(invitation.invited_at)}.</>
            )}
          </p>
        </div>
      </div>

      <div className="px-6 pb-4">
        <Line tone="will">
          It records that you are part of {orgs} portfolio, and lets them propose
          an IP arrangement on a project you have granted them.
        </Line>
        <Line tone="wont">
          It does not grant them any project and shows them none of your content;
          you decide that on this page.
        </Line>
        <Line tone="wont">
          It gives {org} no role in your workspace and no say over your projects.
        </Line>

        <p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11.5px] leading-relaxed text-slate-600">
          <b className="text-slate-900">If this association ends, everything stays yours</b> —
          your projects, documents and analysis are unchanged. {org} simply stops
          seeing what you had shared with them.
        </p>

        <p className="mt-2 rounded-lg border border-iflow-200 bg-iflow-50 px-3 py-2 text-[11.5px] leading-relaxed text-slate-600">
          <b className="text-slate-900">How you got here.</b> This card appeared the
          moment {org} invited the account you are signed in as — there is no link
          to click, no token to hold, and no email that has to be matched to you.
          If the invitation is withdrawn it reads “This invitation is no longer
          available — ask {org} to send a new one”, never nothing at all.
        </p>

        <div className="mt-3">
          {/* ONLY the words the user must type are bold. The figure's label is
              600 with a <b> inside it, so the phrase stands out against the
              instruction around it; ours put font-semibold on the whole label,
              which at this size swallowed the <b> and made all five words look
              equally like something to type. The label is normal weight now and
              the emphasis is carried, unambiguously, by the phrase itself. */}
          <label htmlFor="portfolio-invitation-confirm" className="text-[12px] font-normal text-slate-700">
            Type <b className="font-bold text-slate-900">I understand</b> to confirm
          </label>
          <input
            id="portfolio-invitation-confirm"
            type="text"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-[11.5px] leading-relaxed text-slate-500">
            Nothing applies until you accept. Until then {orgs} page reads
            “Invitation not yet accepted”. You can revisit this any time at
            Account › Sharing.
          </p>
        </div>

        {errorMessage && <p className="mt-2 text-[12.5px] text-rose-600">{errorMessage}</p>}
      </div>

      <div className="flex items-center gap-3 border-t border-slate-200 bg-iflow-50 px-6 py-3">
        <p className="min-w-0 flex-1 text-[11.5px] leading-relaxed text-slate-600">
          Joining a portfolio never shows them your work — sharing is separate,
          and yours to decide.
        </p>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"
        >
          Not now
        </button>
        <button
          type="button"
          onClick={accept}
          disabled={!canAccept}
          className="shrink-0 rounded-lg bg-iflow-600 px-4 py-2 text-[13px] font-bold text-white hover:bg-iflow-700 disabled:opacity-50"
        >
          {accepting ? 'Accepting…' : 'Accept'}
        </button>
      </div>
    </div>
  )
}
